import type { NumberProfile } from "./NameNumerology.data";

type HindiProfileText = Pick<
  NumberProfile,
  "title" | "characteristics" | "strengths" | "weaknesses" | "lucky_color" | "lucky_day" | "career"
>;

/**
 * Hindi text for the nine name-number profiles, keyed by number. The numbers,
 * compatibility lists and planet_hi stay in NameNumerology.data.ts; a test pins
 * that every profile has a Hindi entry with the same list lengths.
 * @internal
 */
export const PROFILE_TEXT_HI: Readonly<Record<number, HindiProfileText>> = {
  1: {
    title: "नेतृत्व करने वाला",
    characteristics: [
      "जन्मजात नेता, दृढ़ इच्छाशक्ति वाला",
      "स्वतंत्र, मौलिक और महत्वाकांक्षी",
      "नए रास्ते बनाने वाला अग्रणी",
      "अपनी पहचान का प्रबल भाव",
    ],
    strengths: ["नेतृत्व", "दृढ़ता", "रचनात्मकता", "आत्मविश्वास"],
    weaknesses: ["ज़िद", "अधीरता", "अहं से लिए निर्णय", "सलाह मानने में कठिनाई"],
    lucky_color: "सुनहरा / नारंगी / पीला",
    lucky_day: "रविवार",
    career: ["उद्यमिता", "राजनीति", "प्रबंधन", "रचनात्मक निर्देशन", "सरकारी सेवा"],
  },
  2: {
    title: "सामंजस्य बनाने वाला",
    characteristics: [
      "संवेदनशील, सहज ज्ञान वाला और सहानुभूतिपूर्ण",
      "स्वभाव से शांति और मध्यस्थता करने वाला",
      "सहयोगी और साथ देने वाला साथी",
      "भावनात्मक समझ गहरी",
    ],
    strengths: ["कूटनीति", "अंतर्ज्ञान", "धैर्य", "सहयोग"],
    weaknesses: ["अति-संवेदनशीलता", "अनिर्णय", "दूसरों पर निर्भरता", "मनोदशा में उतार-चढ़ाव"],
    lucky_color: "सफ़ेद / क्रीम / हल्का हरा",
    lucky_day: "सोमवार",
    career: ["परामर्श", "शिक्षण", "नर्सिंग", "कूटनीति", "कला और संगीत"],
  },
  3: {
    title: "संवाद करने वाला",
    characteristics: [
      "अभिव्यक्त करने वाला, आशावादी और मिलनसार",
      "स्वभाव से मनोरंजक और कहानी सुनाने वाला",
      "कई प्रतिभाओं वाला रचनात्मक मन",
      "आकर्षण से लोगों को जोड़ने वाला",
    ],
    strengths: ["संवाद", "आशावाद", "रचनात्मकता", "आकर्षण"],
    weaknesses: ["बिखरी ऊर्जा", "सतहीपन", "फ़िज़ूलखर्ची", "ध्यान टिकाने में कठिनाई"],
    lucky_color: "पीला / बैंगनी / जामुनी",
    lucky_day: "गुरुवार",
    career: ["लेखन", "अभिनय", "मार्केटिंग", "शिक्षण", "सार्वजनिक भाषण"],
  },
  4: {
    title: "निर्माण करने वाला",
    characteristics: [
      "व्यावहारिक, अनुशासित और व्यवस्थित",
      "मेहनती और लगन वाला",
      "समस्याओं को अलग ढंग से सुलझाने वाला",
      "स्थिरता और व्यवस्था को महत्व देने वाला",
    ],
    strengths: ["अनुशासन", "विश्वसनीयता", "व्यावहारिकता", "लगन"],
    weaknesses: ["कठोरता", "ज़िद", "बदलाव का विरोध", "निराशावाद"],
    lucky_color: "नीला / स्लेटी / खाकी",
    lucky_day: "शनिवार",
    career: ["इंजीनियरिंग", "वास्तुकला", "टेक्नोलॉजी", "शोध", "बैंकिंग"],
  },
  5: {
    title: "खोज करने वाला",
    characteristics: [
      "साहसी, बहुमुखी और तेज़ बुद्धि वाला",
      "स्वतंत्रता और नए अनुभवों से प्रेम",
      "स्वभाव से संवाद और मोलभाव में कुशल",
      "हर परिस्थिति में ढल जाने वाला",
    ],
    strengths: ["अनुकूलन क्षमता", "संवाद", "बहुमुखी प्रतिभा", "जल्दी सीखना"],
    weaknesses: ["बेचैनी", "अस्थिरता", "जोखिम लेना", "प्रतिबद्धता में कठिनाई"],
    lucky_color: "हरा / हल्का भूरा / स्लेटी",
    lucky_day: "बुधवार",
    career: ["व्यापार", "यात्रा उद्योग", "मीडिया", "बिक्री", "परामर्श सेवा"],
  },
  6: {
    title: "पालन-पोषण करने वाला",
    characteristics: [
      "प्रेमपूर्ण, ज़िम्मेदार और सामंजस्यपूर्ण",
      "परिवार और कर्तव्य का प्रबल भाव",
      "कलात्मक रुचि और सौंदर्य-बोध",
      "स्वभाव से देखभाल और उपचार करने वाला",
    ],
    strengths: ["करुणा", "ज़िम्मेदारी", "कलात्मकता", "सामंजस्य"],
    weaknesses: ["आत्म-त्याग", "चिंता", "अधिकार-भाव", "पूर्णतावाद"],
    lucky_color: "सफ़ेद / गुलाबी / हल्का नीला",
    lucky_day: "शुक्रवार",
    career: ["स्वास्थ्य सेवा", "इंटीरियर डिज़ाइन", "आतिथ्य", "फ़ैशन", "समाज सेवा"],
  },
  7: {
    title: "साधक",
    characteristics: [
      "विश्लेषक, आध्यात्मिक और आत्ममंथन करने वाला",
      "हर बात पर गहराई से सोचने वाला",
      "रहस्यों और गूढ़ ज्ञान की ओर आकर्षित",
      "एकांत और भीतर के ज्ञान को महत्व देने वाला",
    ],
    strengths: ["विश्लेषक बुद्धि", "आध्यात्मिकता", "अंतर्ज्ञान", "शोध क्षमता"],
    weaknesses: ["अकेलापन", "दूरी बनाए रखना", "संदेह", "भावनाएँ व्यक्त करने में कठिनाई"],
    lucky_color: "स्लेटी / सफ़ेद / हल्का पीला",
    lucky_day: "सोमवार",
    career: ["शोध", "दर्शन", "आध्यात्म", "मनोविज्ञान", "डेटा साइंस"],
  },
  8: {
    title: "उपलब्धि पाने वाला",
    characteristics: [
      "महत्वाकांक्षी, प्रभावशाली और रणनीतिक",
      "भौतिक प्रगति और व्यापार की गहरी समझ",
      "स्वभाव से प्रबंधक और संगठनकर्ता",
      "शक्ति और धन से कर्म के पाठ",
    ],
    strengths: ["महत्वाकांक्षा", "संगठन", "व्यापारिक समझ", "दृढ़ता"],
    weaknesses: ["काम में डूबे रहना", "भौतिकवाद", "नियंत्रण की प्रवृत्ति", "भावनाओं में कठिनाई"],
    lucky_color: "गहरा नीला / काला / गहरा स्लेटी",
    lucky_day: "शनिवार",
    career: ["वित्त", "रियल एस्टेट", "क़ानून", "कॉर्पोरेट प्रबंधन", "राजनीति"],
  },
  9: {
    title: "योद्धा",
    characteristics: [
      "साहसी, ऊर्जावान और जोशीला",
      "न्याय और उद्देश्य के लिए लड़ने वाला",
      "उदार और मानवतावादी",
      "प्रबल शारीरिक और मानसिक सहनशक्ति",
    ],
    strengths: ["साहस", "उदारता", "नेतृत्व", "जुनून"],
    weaknesses: ["आक्रामकता", "अधीरता", "जल्दबाज़ी", "भावनात्मक उतार-चढ़ाव"],
    lucky_color: "लाल / सिंदूरी / मूँगिया",
    lucky_day: "मंगलवार",
    career: ["सेना / रक्षा", "खेल", "सर्जरी", "सामाजिक कार्य", "उद्यमिता"],
  },
};

/** The profile in the page language: Hindi pages get the Hindi text and the Hindi planet. */
export function localizedProfile(profile: NumberProfile, language: "en" | "hi"): NumberProfile {
  const hi = language === "hi" ? PROFILE_TEXT_HI[profile.number] : undefined;
  return hi ? { ...profile, ...hi, planet: profile.planet_hi } : profile;
}
